import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { tap, catchError } from 'rxjs/operators';
import { environment } from '@env/environment';

export interface HubspotFormField {
	objectTypeId?: string;
	name: string; 
	value: string;	
}

export interface HubspotFormSubmission {
	fields: HubspotFormField[];
	context?: {
		pageUri: string;
		pageName: string;
	};
	legalConsentOptions?: {
		consent: {
			consentToProcess: boolean;
			text: string;
			communications?: Array<{
				value: boolean;
				subscriptionTypeId: number;
				text: string;
			}>;
		};
	};
}

export interface HubspotSubmissionRequest {
	portalId: string;
	formId: string;
	formData: HubspotFormSubmission;
}

@Injectable({
	providedIn: 'root',
})
export class HubspotFacadeService {
	private hubspotBaseUrl = environment.hubspot.baseUrl;

	constructor(private http: HttpClient) {}

	/**
	 * @param request - Hubspot submission request containing portalId, formId, and formData
	 * @returns Observable of the API response
	 */
	private submitToHubspot$(request: HubspotSubmissionRequest): Observable<any> {
		const url = `${this.hubspotBaseUrl}/${request.portalId}/${request.formId}`;
		return this.http.post(url, request.formData);
	}

	/**
	* Submit form data to Hubspot
	 * @param portalId - Hubspot portal ID
	 * @param formId - Hubspot form GUID
	 * @param firstName - User's first name
	 * @param email - User's email
	 * @returns Observable with submission result
	 */
	submitFooterEmailSubscription$(
		portalId: string,
		formId: string,
		firstName: string,
		email: string
	): Observable<any> {
		const formData: HubspotFormSubmission = {
			fields: [
				{
					name: 'firstname',
					value: firstName,
				},
				{
					name: 'email',
					value: email,
				},
			],
			context: {
				pageUri: window.location.href,
				pageName: document.title,
			},
		};

		const request: HubspotSubmissionRequest = {
			portalId,
			formId,
			formData,
		};

		return this.submitToHubspot$(request).pipe(
			tap((response) => {
				console.log('Hubspot form submitted successfully', response);
			}),
			catchError((error) => {
				console.error('Hubspot form submission failed', error);
				throw error;
			})
		);
	}

	/**
	 * Submit form data to Hubspot
	 * @param request - Complete Hubspot submission request
	 * @returns Observable with submission result
	 */
	submitForm$(request: HubspotSubmissionRequest): Observable<any> {
		return this.submitToHubspot$(request).pipe(
			tap((response) => {
				console.log('Hubspot form submitted successfully', response);
			}),
			catchError((error) => {
				console.error('Hubspot form submission failed', error);
				throw error;
			})
		);
	}
}
